/**
 * React Query hooks for Scheduled Tasks API.
 * Admin-only: view task states, toggle enable/disable, trigger manual runs, view run history.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type { ScheduledTaskState, ScheduledTaskRun } from "@gdapwif/shared";

export function useScheduledTasks() {
  const api = useApiClient();
  return useQuery({
    queryKey: ["scheduled-tasks"],
    queryFn: () => api.get<ScheduledTaskState[]>("/api/admin/scheduled-tasks"),
    staleTime: 15_000,
    refetchInterval: 30_000, // Auto-refresh task states every 30s
  });
}

export function useScheduledTaskHistory(taskId: string | null, page = 1, pageSize = 10) {
  const api = useApiClient();
  return useQuery({
    queryKey: ["scheduled-task-history", taskId, { page, pageSize }],
    queryFn: () =>
      api.get<{ items: ScheduledTaskRun[]; total: number; page: number; pageSize: number }>(
        `/api/admin/scheduled-tasks/${taskId}/history?page=${page}&pageSize=${pageSize}`,
      ),
    enabled: !!taskId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useAllTaskHistory(page = 1, pageSize = 10) {
  const api = useApiClient();
  return useQuery({
    queryKey: ["scheduled-task-history", "all", { page, pageSize }],
    queryFn: () =>
      api.get<{ items: ScheduledTaskRun[]; total: number; page: number; pageSize: number }>(
        `/api/admin/scheduled-tasks/history?page=${page}&pageSize=${pageSize}`,
      ),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useToggleScheduledTask() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, enabled }: { taskId: string; enabled: boolean }) =>
      api.patch(`/api/admin/scheduled-tasks/${taskId}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scheduled-tasks"] });
    },
  });
}

export function useRunScheduledTask() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.post(`/api/admin/scheduled-tasks/${taskId}/run`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["scheduled-task-history"] });
    },
  });
}
